import { Fragment, useState } from 'react';
import type { JSX, ReactNode } from 'react';
import { IconPlus, IconSearch } from '../icons/index.js';
import { cn } from '../lib/cn.js';
import { useDnd } from '../lib/dnd.js';
import { Button } from './Button.js';
import { StatusBadge } from './StatusBadge.js';
import type { Couleur } from '@taki/data-model';
import { SelecteurCouleur } from './SelecteurCouleur.js';
import { SettingsInput } from './SettingsControls.js';

/**
 * LES TABLEAUX DU KIT PARAMETRES (voir `Settings.tsx`) : le petit tableau a
 * poignees, et celui des Étiquettes et listes. La poignee est celle du Studio
 * (`useDnd`) : souris, ou clavier (Espace puis fleches, Echap annule).
 */

const FOCUS = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-border';
const LIGNE_DND =
  'border-t border-border-soft data-[dnd-etat=saisi]:opacity-40 data-[dnd-depot=avant]:border-t-2 data-[dnd-depot=avant]:border-t-ink-400 data-[dnd-depot=apres]:border-b-2 data-[dnd-depot=apres]:border-b-ink-400';
const AIDE_POIGNEE = 'Glisser pour déplacer. Au clavier : Espace puis flèches.';

export interface SettingsTableColumn {
  label: string;
  /** En-tete lu par le lecteur d'ecran seulement. */
  cache?: boolean;
  /** Colonne alignee a droite (un interrupteur, un bouton). */
  fin?: boolean;
  /** Largeur fixe (classe Tailwind, ex. `w-28`). */
  largeur?: string;
}

export interface SettingsTableRow {
  id: string;
  /** Une cellule par colonne, dans l'ordre des colonnes. */
  cells: readonly ReactNode[];
  /** Contenu deplie sous la ligne (edition sur place). */
  detail?: ReactNode;
  /** Ligne en retrait (inactive, en attente). */
  attenue?: boolean;
}

export interface SettingsTableProps {
  /** Libelle accessible du tableau. */
  label: string;
  columns: readonly SettingsTableColumn[];
  rows: readonly SettingsTableRow[];
  /** Present : poignee de reordre en tete de ligne. Recoit le nouvel ordre des ids. */
  onReorder?: (ids: string[]) => void;
  /** Nom d'une ligne, pour l'annonce du deplacement. */
  nomDe?: (id: string) => string;
  /** Texte quand aucune ligne. */
  empty?: string;
  /** Sous le tableau, dans le cadre : une ligne d'ajout. */
  footer?: ReactNode;
  /** Present : bouton × en fin de ligne. */
  onRemove?: (id: string) => void;
  /** Libelle accessible du retrait d'une ligne. Defaut : « Retirer <nom> ». */
  removeLabel?: (id: string) => string;
  /** Tableau grise, plus de deplacement. */
  disabled?: boolean;
}

/** Le × de fin de ligne (ou ↶ pour retablir). */
function BoutonRetrait({ label, retablir = false, disabled, onClick }: {
  label: string;
  retablir?: boolean;
  disabled: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      disabled={disabled}
      aria-label={label}
      title={retablir ? 'Rétablir' : 'Retirer'}
      onClick={onClick}
      className={cn('rounded-sm px-1.5 py-1 text-base text-ink-400 hover:bg-surface-1 hover:text-ink-900', FOCUS)}
    >
      {retablir ? '↶' : '×'}
    </button>
  );
}

/** Petit tableau des parametres, poignees de reordre en option. */
export function SettingsTable({
  label,
  columns,
  rows,
  onReorder,
  nomDe,
  empty,
  footer,
  onRemove,
  removeLabel,
  disabled = false,
}: SettingsTableProps): JSX.Element {
  const ids = rows.map((row) => row.id);
  const dnd = useDnd(
    'champ',
    ids,
    (ordre) => {
      if (!disabled) onReorder?.(ordre);
    },
    nomDe,
  );
  const poignees = onReorder !== undefined;
  const nbColonnes = columns.length + (poignees ? 1 : 0) + (onRemove ? 1 : 0);
  return (
    <div className="mt-3">
      <div className={cn('overflow-x-auto rounded-sm border border-border', disabled && 'opacity-60')}>
        <table aria-label={label} className="w-full border-collapse text-left text-[0.8125rem]">
          <thead className="bg-surface-1 text-xs font-medium text-ink-500">
            <tr>
              {poignees ? <th className="w-8"><span className="sr-only">Ordre</span></th> : null}
              {columns.map((column, index) => (
                <th
                  key={index}
                  className={cn('px-2 py-2 font-medium', column.fin && 'whitespace-nowrap px-3 text-right', column.largeur)}
                >
                  {column.cache ? <span className="sr-only">{column.label}</span> : column.label}
                </th>
              ))}
              {onRemove ? <th className="w-8"><span className="sr-only">Retirer</span></th> : null}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const { ligne, poignee } = dnd.propsDe(row.id);
              return (
                <Fragment key={row.id}>
                  <tr {...(poignees ? ligne : {})} className={cn(LIGNE_DND, row.attenue && 'text-ink-500')}>
                    {poignees ? (
                      <td className="pl-2">
                        <button
                          type="button"
                          {...poignee}
                          disabled={disabled}
                          draggable={!disabled}
                          title={AIDE_POIGNEE}
                          className={cn('cursor-grab rounded-sm px-1 py-2 text-base text-ink-400 active:cursor-grabbing', FOCUS)}
                        >
                          ⠿
                        </button>
                      </td>
                    ) : null}
                    {row.cells.map((cell, index) => (
                      <td key={index} className={cn('px-2 py-1.5', columns[index]?.fin && 'px-3 text-right')}>
                        {columns[index]?.fin ? <span className="flex items-center justify-end gap-1">{cell}</span> : cell}
                      </td>
                    ))}
                    {onRemove ? (
                      <td className="pr-2">
                        <BoutonRetrait
                          disabled={disabled}
                          label={removeLabel?.(row.id) ?? `Retirer ${nomDe?.(row.id) ?? row.id}`}
                          onClick={() => onRemove(row.id)}
                        />
                      </td>
                    ) : null}
                  </tr>
                  {row.detail != null ? (
                    <tr className="border-t border-border-soft">
                      <td colSpan={nbColonnes} className="px-3 pb-3">
                        {row.detail}
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              );
            })}
            {rows.length === 0 && empty !== undefined ? (
              <tr>
                <td colSpan={nbColonnes} className="px-3 py-6 text-center text-sm text-ink-500">
                  {empty}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
        {footer != null ? <div className="flex items-center gap-2 border-t border-border-soft px-2 py-2">{footer}</div> : null}
      </div>
      {poignees ? <span className="sr-only" role="status">{dnd.annonce}</span> : null}
    </div>
  );
}

/** Une valeur d'une liste de choix ou d'une etiquette. */
export interface SettingsValue {
  id: string;
  label: string;
  /** Une clef de la grille du design ou un hex (`SelecteurCouleur`). */
  tone: Couleur;
  /** Etat en attente, a cote du badge (« À archiver », « Archivée »). */
  note?: string;
  /** Badge en retrait (archivee, a retirer). */
  attenue?: boolean;
  /** Le bouton de fin de ligne retablit au lieu de retirer. */
  retablir?: boolean;
  /** La valeur du groupe (`SettingsValuesProps.groupes`) ; vide : tous. */
  groupe?: string;
}


export interface SettingsValuesProps {
  values: readonly SettingsValue[];
  onReorder: (ids: string[]) => void;
  onRename: (id: string, label: string) => void;
  onTone: (id: string, tone: Couleur) => void;
  /** Retirer, ou retablir quand la valeur porte `retablir`. */
  onRemove: (id: string) => void;
  /** Ajoute une valeur ; rend une erreur a afficher, ou null. */
  onAdd: (label: string) => string | null;
  /**
   * UNE LISTE DONT LES VALEURS APPARTIENNENT A UNE AUTRE (le Job a son Pole,
   * `dependDe`) : une colonne de plus, le groupe de chaque valeur, « Tous »
   * en premier. Absent : pas de colonne.
   */
  groupes?: { label: string; options: readonly { value: string; label: string }[] };
  onGroupe?: (id: string, groupe: string | null) => void;
  disabled?: boolean;
}

/** Étiquettes et listes : recherche, poignee, badge, couleur, retrait, ajout. */
export function SettingsValues({
  values,
  onReorder,
  onRename,
  onTone,
  onRemove,
  onAdd,
  groupes,
  onGroupe,
  disabled = false,
}: SettingsValuesProps): JSX.Element {
  const [recherche, setRecherche] = useState('');
  const [edition, setEdition] = useState<string | null>(null);
  const [ajout, setAjout] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const terme = recherche.trim().toLocaleLowerCase();
  const visibles = terme === '' ? values : values.filter((value) => value.label.toLocaleLowerCase().includes(terme));
  const ajouter = (): void => {
    if (disabled) return;
    const label = ajout.trim();
    if (!label) {
      setErreur('Champ obligatoire');
      return;
    }
    const refus = onAdd(label);
    setErreur(refus);
    if (refus === null) {
      setAjout('');
      setRecherche('');
    }
  };
  /* LE REORDRE PORTE SUR LA LISTE ENTIERE : pendant une recherche, les lignes
     cachees gardent leur rang, les visibles s'echangent entre elles. */
  const reordonner = (ordreVisible: string[]): void => {
    const visiblesIds = new Set(ordreVisible);
    let rang = 0;
    onReorder(values.map((value) => (visiblesIds.has(value.id) ? ordreVisible[rang++]! : value.id)));
  };
  return (
    <div>
      <div className="mt-4">
        <SettingsInput
          leftIcon={<IconSearch size={15} />}
          aria-label="Rechercher une valeur"
          placeholder="Rechercher une valeur"
          value={recherche}
          onChange={(event) => setRecherche(event.target.value)}
        />
      </div>
      <SettingsTable
        label="Valeurs"
        disabled={disabled}
        columns={[
          { label: 'Valeur' },
          ...(groupes ? [{ label: groupes.label, largeur: 'w-32' }] : []),
          { label: 'Couleur', largeur: 'w-16 text-center' },
          { label: 'Retirer', cache: true, largeur: 'w-8' },
        ]}
        onReorder={reordonner}
        nomDe={(id) => values.find((value) => value.id === id)?.label ?? id}
        empty={values.length ? 'Aucune valeur trouvée.' : 'Aucune valeur.'}
        rows={visibles.map((value) => ({
          id: value.id,
          cells: [
            <span key="valeur" className="flex min-w-[8rem] flex-wrap items-center gap-2">
              {edition === value.id ? (
                <SettingsInput
                  autoFocus
                  className="px-2"
                  aria-label={`Libellé de ${value.label}`}
                  value={value.label}
                  onChange={(event) => onRename(value.id, event.target.value)}
                  onBlur={() => setEdition(null)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === 'Escape') setEdition(null);
                  }}
                />
              ) : (
                <button
                  type="button"
                  disabled={disabled}
                  aria-label={`Modifier ${value.label}`}
                  onClick={() => setEdition(value.id)}
                  className={cn('rounded-sm', FOCUS)}
                >
                  <StatusBadge tone={value.tone} label={value.label || 'Sans nom'} className={cn('h-7', value.attenue && 'opacity-50')} />
                </button>
              )}
              {value.note ? <span className="text-[0.6875rem] text-ink-400">{value.note}</span> : null}
            </span>,
            ...(groupes ? [
              <select
                key="groupe"
                aria-label={`${groupes.label} de ${value.label}`}
                value={value.groupe ?? ''}
                disabled={disabled || onGroupe === undefined}
                onChange={(event) => onGroupe?.(value.id, event.target.value === '' ? null : event.target.value)}
                className={cn('h-7 w-full cursor-pointer rounded-sm border border-border bg-surface px-1 text-xs text-ink-900 disabled:cursor-not-allowed', FOCUS)}
              >
                <option value="">Tous</option>
                {groupes.options.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>,
            ] : []),
            <span key="couleur" className="flex justify-center">
              <SelecteurCouleur
                label={`Couleur de ${value.label}`}
                value={value.tone}
                disabled={disabled}
                onChange={(couleur) => onTone(value.id, couleur)}
              />
            </span>,
            <BoutonRetrait
              key="retirer"
              disabled={disabled}
              retablir={value.retablir === true}
              label={value.retablir ? `Rétablir ${value.label}` : `Retirer ${value.label}`}
              onClick={() => onRemove(value.id)}
            />,
          ],
        }))}
        footer={
          <>
            <SettingsInput
              className="flex-1 border-transparent bg-transparent px-2"
              placeholder="Nouvelle valeur"
              aria-label="Nouvelle valeur"
              value={ajout}
              disabled={disabled}
              onChange={(event) => {
                setAjout(event.target.value);
                setErreur(null);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  ajouter();
                }
              }}
            />
            <Button variant="secondary" size="sm" disabled={disabled} onClick={ajouter}>
              <IconPlus size={14} />
              Ajouter
            </Button>
          </>
        }
      />
      {erreur ? (
        <p role="alert" className="mt-2 text-xs text-danger">
          {erreur}
        </p>
      ) : null}
    </div>
  );
}
